// The owner and vehicle document pack: grouping, selection, filenames and the
// two CSVs that go in the zip.
//
// DELIBERATELY FREE OF RUNTIME IMPORTS. web/test/documentPack.test.ts loads this
// module under `node --test --experimental-strip-types`, which resolves ESM
// specifiers literally, so a relative import here would stop the test loading it
// at all. That is why the CSV helpers are written out below instead of shared
// with utils/zip.ts. Type-only imports would be fine, since strip-types erases
// them; runtime ones are not.
//
// What the zip contains, and why it is CSV rather than PDF. One CSV per selected
// extract, named for the plate, the month and the line, plus one manifest at the
// root. An owner asked to check an insurance charge wants the rows behind it in
// something he can sort and add up, and a CSV opens in every spreadsheet on
// every phone. Rendering a PDF in the browser would need a layout engine in the
// bundle and would produce something harder to check, not easier.

// The fields the download needs from statements/getOwnerDocumentPack. Kept
// narrow on purpose: this module must not grow a dependency on the whole row.
export type PackRow = {
  owner_name: string | null
  vehicle_id: string
  plate: string | null
  period_start: string
  month: string | null
  line_code: string
  line_label: string | null
  document_title: string | null
  supplier: string | null
  document_number: string | null
  document_kind: string | null
  document_zar: number | null
  statement_zar: number | null
  variance_zar: number | null
  extract_token: string | null
  redaction_note: string | null
  source_shareable: boolean
  source_storage_path: string | null
  extract_id: string
  is_draft: boolean
}

/** One extract's contents, as statements/getEvidenceExtract returns them. */
export type PackExtract = {
  plate: string
  period_start: string
  line_code: string
  title: string
  rows: Array<Record<string, unknown>>
  row_count: number
  total_zar: number | null
  basis_note: string | null
  redaction_note: string | null
  supplier: string | null
  document_number: string | null
  source_covers_vehicles: number | null
  built_at: string
}

/**
 * The identity of a row for selection. extract_id is the primary key of
 * evidence_extract, so it is unique across the whole pack and stays stable when
 * the list is refetched. Selection keyed on an array index would silently move
 * to a different document the moment the order changed.
 */
export function rowKey(row: PackRow): string {
  return row.extract_id
}

// -- Grouping --------------------------------------------------------------

export type PackMonthGroup = {
  key: string
  month: string
  period_start: string
  rows: PackRow[]
}

export type PackVehicleGroup = {
  key: string
  plate: string
  rows: PackRow[]
  months: PackMonthGroup[]
  document_total_zar: number
}

/**
 * Grouped by car, then by month, newest month first.
 *
 * The owner dialog shows several cars and the vehicle dialog shows one, and both
 * read better this way than as a flat list of thirty rows: an owner checking
 * August looks for August, not for row 14.
 */
export function groupPack(rows: PackRow[]): PackVehicleGroup[] {
  const byVehicle = new Map<string, PackVehicleGroup>()

  for (const row of rows) {
    const key = row.vehicle_id
    let group = byVehicle.get(key)
    if (!group) {
      group = {
        key,
        plate: row.plate ?? 'Unknown vehicle',
        rows: [],
        months: [],
        document_total_zar: 0,
      }
      byVehicle.set(key, group)
    }
    group.rows.push(row)
    // Null is not zero. A document with no total recorded must not add zero and
    // read as though it had been counted.
    if (row.document_zar !== null) group.document_total_zar += row.document_zar
  }

  for (const group of byVehicle.values()) {
    const byMonth = new Map<string, PackMonthGroup>()
    for (const row of group.rows) {
      let month = byMonth.get(row.period_start)
      if (!month) {
        month = {
          key: row.period_start,
          month: row.month ?? row.period_start,
          period_start: row.period_start,
          rows: [],
        }
        byMonth.set(row.period_start, month)
      }
      month.rows.push(row)
    }
    group.months = [...byMonth.values()].sort((a, b) =>
      b.period_start.localeCompare(a.period_start),
    )
  }

  return [...byVehicle.values()].sort((a, b) => a.plate.localeCompare(b.plate))
}

// -- Selection -------------------------------------------------------------

/** Toggles one row, returning a new set rather than mutating the old one. */
export function toggleKey(selected: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(selected)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  return next
}

/** Toggles a whole group: every row on unless they are all already on. */
export function toggleGroup(
  selected: ReadonlySet<string>,
  keys: readonly string[],
): Set<string> {
  const next = new Set(selected)
  const allOn = keys.length > 0 && keys.every((k) => next.has(k))
  for (const k of keys) {
    if (allOn) next.delete(k)
    else next.add(k)
  }
  return next
}

export type TriState = 'on' | 'off' | 'some'

/**
 * Whether a set of keys is fully, partly or not selected.
 *
 * An empty group is 'off', never 'on'. Calling it 'on' is how a select-all
 * checkbox ends up ticked over an empty list, which tells the operator
 * everything is chosen when nothing is.
 */
export function groupState(selected: ReadonlySet<string>, keys: readonly string[]): TriState {
  if (keys.length === 0) return 'off'
  let on = 0
  for (const k of keys) if (selected.has(k)) on++
  if (on === 0) return 'off'
  return on === keys.length ? 'on' : 'some'
}

export function allKeys(rows: readonly PackRow[]): string[] {
  return rows.map(rowKey)
}

/** The rows currently ticked, in the list's own order. */
export function selectedRows(rows: readonly PackRow[], selected: ReadonlySet<string>): PackRow[] {
  return rows.filter((r) => selected.has(rowKey(r)))
}

// -- Filenames -------------------------------------------------------------

/** Safe on Windows too, which refuses \ / : * ? " < > | and a trailing dot. */
export function safeFileName(name: string): string {
  return (
    name
      .replace(/[\\/:*?"<>|]/g, '-')
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\.+$/, '')
      .slice(0, 120) || 'untitled'
  )
}

/** 2026-04-01 -> 2026-04. Anything unexpected is passed through untouched. */
export function monthSlug(periodStart: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(periodStart)
  return m ? `${m[1]}-${m[2]}` : periodStart
}

/**
 * One extract's file inside the zip, carrying plate, month and line code, which
 * is what somebody looking for one document in a folder of thirty searches on.
 */
export function extractFileName(row: PackRow): string {
  const plate = row.plate ?? 'unknown-vehicle'
  return safeFileName(`${plate}/${monthSlug(row.period_start)}_${row.line_code}.csv`)
}

/** The archive's own name. */
export function packFileName(scopeLabel: string, count: number): string {
  const stamp = new Date().toISOString().slice(0, 10)
  return safeFileName(`${scopeLabel}-documents-${count}-items-${stamp}`) + '.zip'
}

// -- CSV -------------------------------------------------------------------

/** RFC 4180: a field holding a comma, a quote or a newline is quoted. */
export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return ''
  const s = typeof value === 'string' ? value : String(value)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/**
 * Rows to CSV text, with a UTF-8 byte order mark.
 *
 * The BOM is not decoration. Excel on Windows reads a CSV without one in the
 * system codepage, so an owner's name with an accent arrives mangled. Numbers,
 * LibreOffice and Google Sheets all skip it.
 */
export function toCsv(rows: Array<Array<unknown>>): string {
  return '﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n'
}

// -- What the zip says about each document ---------------------------------

/**
 * The redaction status of one row, in the words the manifest uses.
 *
 * THREE STATES, and the middle one is the one that matters. The single-car
 * extract always goes out: it was assembled from this vehicle's own rows, so no
 * other owner's figures were ever in it. The supplier document behind it usually
 * does not: Auto & General's schedule covers 17 vehicles and Cartrack's covers
 * 42, and a document that names other people's cars is not this owner's to
 * receive. Of 1,822 rows in the pack, 16 have a supplier document that names only
 * one car, and none of those 16 has a file on record, so today the third state is
 * a statement of fact rather than an attachment.
 *
 * A withheld document is always NAMED, with the reason. Dropping it silently is
 * the failure this whole surface exists to end: the owner cannot ask about a line
 * he was never shown.
 */
export function sourceStatus(row: PackRow): string {
  if (!row.source_shareable) {
    return 'Withheld: the supplier document covers other vehicles on the same account'
  }
  if (!row.source_storage_path) {
    return 'Shareable, but no file is held on record for it'
  }
  return 'Included'
}

/** Whether a supplier file will actually be added to the archive for this row. */
export function includesSourceFile(row: PackRow): boolean {
  return row.source_shareable && !!row.source_storage_path
}

const MANIFEST_HEADER = [
  'Vehicle',
  'Month',
  'Line item',
  'Document',
  'Supplier',
  'Document number',
  'Document total (ZAR)',
  'Statement figure (ZAR)',
  'Difference (ZAR)',
  'Statement status',
  'File in this archive',
  'Supplier document',
  'How this was redacted',
]

/**
 * The manifest at the root of the zip: every selected item, what file carries
 * it, and what was withheld and why.
 */
export function manifestCsv(
  rows: readonly PackRow[],
  scopeLabel: string,
  failures: ReadonlyMap<string, string> = new Map(),
): string {
  const out: Array<Array<unknown>> = []
  out.push([`Fleetly document pack: ${scopeLabel}`])
  out.push([`Prepared ${new Date().toISOString().slice(0, 10)}`, `${rows.length} items`])
  out.push([
    'Each CSV in this archive was built from one vehicle’s own rows. Another owner’s figures were never in it.',
  ])
  out.push([])
  out.push(MANIFEST_HEADER)

  for (const row of rows) {
    const failed = failures.get(row.extract_id)
    out.push([
      row.plate ?? 'Unknown vehicle',
      row.month ?? row.period_start,
      row.line_label ?? row.line_code,
      row.document_title ?? '',
      row.supplier ?? '',
      row.document_number ?? '',
      row.document_zar === null ? '' : row.document_zar.toFixed(2),
      row.statement_zar === null ? '' : row.statement_zar.toFixed(2),
      row.variance_zar === null ? '' : row.variance_zar.toFixed(2),
      row.is_draft ? 'Draft statement' : 'Issued',
      failed ? `Not included: ${failed}` : extractFileName(row),
      sourceStatus(row),
      row.redaction_note ?? '',
    ])
  }

  return toCsv(out)
}

/**
 * One extract as a CSV a person can read: a short head saying what the document
 * is and how it was redacted, then its rows.
 *
 * Column order follows the extract's own first row rather than a fixed list, so
 * a new field added to an extract appears here instead of being dropped.
 */
export function extractCsv(row: PackRow, extract: PackExtract): string {
  const out: Array<Array<unknown>> = []
  out.push([extract.title || row.document_title || 'Evidence extract'])
  out.push(['Vehicle', row.plate ?? extract.plate])
  out.push(['Month', row.month ?? row.period_start])
  out.push(['Line item', row.line_label ?? row.line_code])
  if (extract.supplier) out.push(['Supplier', extract.supplier])
  if (extract.document_number) out.push(['Document number', extract.document_number])
  out.push([
    'Document total (ZAR)',
    extract.total_zar === null ? '' : extract.total_zar.toFixed(2),
  ])
  if (row.statement_zar !== null) {
    out.push(['Statement figure (ZAR)', row.statement_zar.toFixed(2)])
  }
  if (row.variance_zar !== null) out.push(['Difference (ZAR)', row.variance_zar.toFixed(2)])
  if (extract.basis_note) out.push(['How this was worked out', extract.basis_note])
  if (extract.redaction_note) out.push(['Redaction', extract.redaction_note])
  if (extract.source_covers_vehicles !== null) {
    out.push(['Vehicles on the supplier document', extract.source_covers_vehicles])
  }
  out.push(['Rows', extract.row_count])
  out.push([])

  const keys: string[] = []
  for (const r of extract.rows) {
    for (const k of Object.keys(r)) if (!keys.includes(k)) keys.push(k)
  }

  if (keys.length === 0) {
    out.push(['This extract holds no line detail. The total above is the whole record.'])
  } else {
    out.push(keys.map(humanHeading))
    for (const r of extract.rows) out.push(keys.map((k) => scalar(r[k])))
  }

  return toCsv(out)
}

/** amount_incl_vat_zar -> "Amount incl vat zar". Debug-looking keys read badly. */
export function humanHeading(key: string): string {
  const words = key.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** A cell value flattened for CSV. An object becomes JSON rather than "[object Object]". */
function scalar(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return ''
    }
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

/**
 * The sentence under a disabled download button.
 *
 * Never "0 documents". The operator needs to know WHICH of the two reasons it is:
 * this owner has no documents on record at all, or he has some and none are
 * ticked. Those call for different actions.
 */
export function disabledReason(total: number, selectedCount: number): string | null {
  if (total === 0) {
    return 'There are no documents on record behind this statement yet, so there is nothing to download.'
  }
  if (selectedCount === 0) {
    return 'Tick the documents you want, or choose Select all.'
  }
  return null
}
